import { InAppState } from "../base/InAppState";
import template from "./GuestAccountDetailTemplate.html";
import { _lcs, locale } from "../../i18n";
import { GuestFormat } from "./GuestFormat";

// Survey channel key -> label (German source strings, translated where read).
const CHANNEL_LABELS = {
    link : "Per Link",
    qr   : "Per QR-Code",
    chat : "Im Chat",
    menu : "Aus der Gäste-App",
    push : "Per Benachrichtigung"
};

/**
 * GuestAccountDetail — one guest account across the platform: the houses it
 * signed in at, its chat with each, its plans and the survey invitations that
 * named it, with how far each got (sent / opened / completed).
 *
 * Read-only. The answers themselves are not shown here either: the guest-app
 * promises they are anonymous, and the API never hands them out per person
 * (see core-api GuestOverviewService).
 */
export class GuestAccountDetail extends InAppState
{
    static ROUTE = '/guest/users/:userId';
    static ID = 'guest-user-detail';

    async enter()
    {
        this.detail = null;

        this.addEventListener( this.app.view.pageContent, "click", ( e ) => this.onAction( e ) );

        this.showMessage( "user-round", _lcs( "Lade Gästekonto …" ), _lcs( "Einen Moment bitte." ) );

        let status = 0;
        try
        {
            const { result } = await this.app.api.get(
                "/admin/guest/" + encodeURIComponent( this.getParam( "userId" ) ) );
            if ( !result || +result.status !== 200 || !result.data?.guest )
            {
                throw new Error( "Unexpected /admin/guest/:id response." );
            }
            this.detail = result.data;
        }
        catch ( e )
        {
            status = +e?.status || 0;
            console.error( e );
        }

        if ( !this.isActive() )
        {
            return;
        }
        if ( !this.detail )
        {
            this.showMessage( "triangle-alert",
                status === 404 ? _lcs( "Gästekonto nicht gefunden" ) : _lcs( "Gästekonto konnte nicht geladen werden" ),
                status === 404 ? _lcs( "Dieses Konto gibt es nicht (mehr) oder es ist kein Gästekonto." ) : _lcs( "Bitte versuche es später erneut." ),
                true );
            return;
        }

        // The topbar shows the guest's name now that it is known.
        this.app.view.updateContentHeader();
        this.render();
    }

    render()
    {
        const d = this.detail;
        const guestMessages = d.conversations.reduce( ( n, c ) =>
            n + ( c.messages || [] ).filter( ( m ) => m.sender === "guest" ).length, 0 );

        this.app.view.render( this.app.view.pageContent, template, {
            "guest" : {
                ...d.guest,
                "initials" : GuestFormat.initials( d.guest.name ),
                "statusHtml" : GuestFormat.statusPill( d.guest.status ),
                "registered" : GuestFormat.dateTime( d.guest.registeredAt ) || "—",
                "roles" : Array.isArray( d.guest.roles ) ? d.guest.roles : []
            },
            "stats" : {
                "houses" : d.accommodations.length,
                "messages" : guestMessages,
                "plans" : d.plans.length,
                "surveys" : d.surveyInvitations.length
            },
            "houses" : d.accommodations.map( ( a ) => ( {
                "name" : a.name || _lcs( "Gelöschte Unterkunft" ),
                "slug" : a.slug,
                "firstSeen" : GuestFormat.dateTime( a.firstSeenAt ) || "—",
                "lastSeen" : GuestFormat.dateTime( a.lastSeenAt ) || "—"
            } ) ),
            "chats" : d.conversations.map( ( c ) => ( {
                "house" : c.accommodationName || _lcs( "Gelöschte Unterkunft" ),
                "lines" : GuestAccountDetail.linesOf( c.messages || [] ),
                "unread" : +c.unreadCount || 0
            } ) ),
            "plans" : d.plans.map( ( p ) => ( {
                "name" : p.name,
                "house" : p.accommodationName || "—",
                "stay" : GuestFormat.stay( p ),
                "itemCount" : +p.itemCount || 0
            } ) ),
            "surveys" : d.surveyInvitations.map( ( i ) => ( {
                "title" : i.surveyTitle,
                "house" : i.accommodationName || "—",
                "channel" : CHANNEL_LABELS[ i.channel ] ? _lcs( CHANNEL_LABELS[ i.channel ] ) : i.channel,
                "sent" : GuestFormat.dateTime( i.createdAt ),
                "state" : i.completedAt ? "completed" : ( i.openedAt ? "opened" : "sent" )
            } ) )
        } );

        this.app.view.pageContent.querySelectorAll( '[data-id="chat-scroll"]' )
            .forEach( ( el ) => { el.scrollTop = el.scrollHeight; } );
    }

    onAction( e )
    {
        const el = e.target.closest( "[data-action]" );
        if ( !el )
        {
            return;
        }
        e.preventDefault();
        if ( el.dataset.action === "verify" )
        {
            this.verify( el );
            return;
        }
        if ( el.dataset.action === "back" )
        {
            this.app.router.redirect( "/guest/users" );
        }
    }

    /**
     * Verify the account by hand — for a guest whose confirmation mail never
     * arrived. Only offered while the account is "not_validated"; blocked or
     * banned accounts are not unlocked this way (the API refuses with 409).
     */
    async verify( button )
    {
        const guest = this.detail?.guest;
        if ( !guest || guest.status !== "not_validated" )
        {
            return;
        }

        const confirmed = await this.app.view.confirm(
            _lcs( "Gästekonto verifizieren?" ),
            _lcs( "„{email}“ wird als bestätigt markiert, ohne dass der Gast den Link aus der Bestätigungs-E-Mail geöffnet hat.", { email: guest.email } ),
            _lcs( "Verifizieren" ),
            _lcs( "Abbrechen" )
        );
        if ( !confirmed || !this.isActive() )
        {
            return;
        }

        button.disabled = true;
        try
        {
            const { result } = await this.app.api.post(
                "/admin/guest/" + encodeURIComponent( guest.id ) + "/verify", {} );
            if ( !result || +result.status !== 200 || !result.data?.status )
            {
                throw new Error( "Unexpected /admin/guest/:id/verify response." );
            }
            if ( !this.isActive() )
            {
                return;
            }
            guest.status = result.data.status;
            guest.isValidated = !!result.data.isValidated;
            this.render();
            this.app.view.toast( _lcs( "„{name}“ ist jetzt verifiziert.", { name: guest.name } ), "success" );
        }
        catch ( err )
        {
            console.error( err );
            button.disabled = false;
            this.app.view.toast( +err?.status === 409
                ? _lcs( "Dieses Konto wartet nicht (mehr) auf eine Verifizierung." )
                : _lcs( "Das Konto konnte nicht verifiziert werden. Bitte versuche es später erneut." ), "error" );
        }
    }

    /**
     * A thread as lines: messages, with a day separator wherever it crosses
     * into a new day. "me" is the house (host or AI concierge), as in the
     * client-admin's inbox.
     */
    static linesOf( messages )
    {
        const lines = [];
        let lastDay = null;

        messages.forEach( ( message ) =>
        {
            const sentAt = new Date( message.createdAt );
            const day = sentAt.toDateString();
            if ( day !== lastDay )
            {
                lines.push( { "type" : "day", "text" : sentAt.toLocaleDateString( locale(),
                    { weekday : "short", day : "2-digit", month : "long", year : "numeric" } ) } );
                lastDay = day;
            }

            if ( message.sender === "system" )
            {
                lines.push( { "type" : "sys", "text" : message.body } );
                return;
            }

            lines.push( {
                "type" : "message",
                "from" : message.sender === "guest" ? "them" : "me",
                "isConcierge" : message.sender === "concierge",
                "text" : message.body,
                "time" : sentAt.toLocaleTimeString( locale(), { hour : "2-digit", minute : "2-digit" } )
            } );
        } );

        return lines;
    }

    /** Centered icon + heading + text (loading / error); `back` adds a way out. */
    showMessage( icon, title, text, back = false )
    {
        this.app.view.pageContent.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
                ( back
                    ? '<button type="button" class="btn btn--ghost btn--sm" data-action="back">' +
                        '<i class="icon icon--arrow-left"></i> ' + GuestFormat.esc( _lcs( "Alle Gästekonten" ) ) + '</button>'
                    : '' ) +
            '</div>';
        // textContent assignment avoids HTML injection from dynamic strings.
        this.app.view.pageContent.querySelector( "h2" ).textContent = title;
        this.app.view.pageContent.querySelector( "p" ).textContent = text;
    }

    getContentHeaderData()
    {
        return {
            title: this.detail ? this.detail.guest.name : _lcs( "Gästekonto" ),
            subtitle: _lcs( "Gästekonten" )
        };
    }
}
